import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
	getAttentionDashboard,
	updateAttentionDashboard,
} from '../../thunks/attention_dashboard_thunks';
import { Card, Row, Spin } from 'antd';
import EachWidget from '../attention_dashboard_components/EachWidget';
import ADHeader from '../attention_dashboard_components/ADHeader';
import ADAddWidgetPopup from '../attention_dashboard_components/ADAddWidgetPopup';
import { ROLE_TYPES, SORTING_PROPS } from '../../utils/DataConstants';
import { savedFilterViewsRestCrudThunksForBuyer } from '../../thunks/saved_filter_views_thunks';
import AttentionDashboardEditPopup from '../attention_dashboard_components/AttentionDashboardEditPopup';
import OverlayContainer from '../common/OverlayContainer';
import { getObjectValues, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import DashboardKPICard from '../dashboard_KPI_card/DashboardKPICard';
import moment from 'moment';
import { kpiDates, kpis } from '../../constants/KPIs';
import DashboardDateRangePicker from '../dashboard_date_range_picker/DashboardDateRangePicker';
import ViewSelection from '../view_selection/ViewSelection';
import AttentionDashboardFilterConfig from '../attention_dashboard_filter_config/AttentionDashboardFilterConfig';

const invalidFilters = [
	'sort_by',
	'order',
	'hasBuyerArchived',
	'hasSupplierArchived',
	'isStale',
	'createdAtStartDate',
	'createdAtEndDate',
	'completedAtStartDate',
	'completedAtEndDate',
];

const cleanFilters = (filters = {}) => {
	invalidFilters.forEach((invalidFilter) => delete filters[invalidFilter]);
	return filters;
};

const AttentionDashboardIndexPage = ({
	userType,
	currentUser,
	attentionDashboard,
	getAttentionDashboard,
	saveAttentionDashboard,
	updateSavedFilterView,
}): React.ReactElement => {
	const [dashboardViewsLoading, setDashboardViewsLoading] = useState(false);
	const [addWidgetModalVisible, setAddWidgetModalVisible] = useState(false);
	const [editWidgetModalVisible, setEditWidgetModalVisible] = useState(false);
	const [startDate, setStartDate] = useState(moment().subtract(1, 'months'));
	const [endDate, setEndDate] = useState(moment());
	const [view, setView] = useState();

	const showAddWidgetModal = useCallback(() => setAddWidgetModalVisible(true), []);
	const hideAddWidgetModal = useCallback(() => setAddWidgetModalVisible(false), []);
	const showEditModal = useCallback(() => setEditWidgetModalVisible(true), []);
	const hideEditModal = useCallback(() => setEditWidgetModalVisible(false), []);

	const refreshDashboard = useCallback(() => {
		setDashboardViewsLoading(true);
		getAttentionDashboard().then((view) => {
			setView(
				JSON.parse(JSON.stringify(nullSafeGetOrElse('defaultWorkOrderFilterView', view, null)))
			);
			setDashboardViewsLoading(false);
		});
	}, [getAttentionDashboard]);

	useEffect(() => {
		refreshDashboard();
	}, [refreshDashboard]);

	const onSaveDashboard = useCallback(
		(savedFilterViewIds) => {
			hideEditModal();
			setDashboardViewsLoading(true);
			saveAttentionDashboard({
				...attentionDashboard,
				savedFilterViewIds,
			}).then(() => setDashboardViewsLoading(false));
		},
		[hideEditModal, attentionDashboard, saveAttentionDashboard]
	);
	const KPIs = useMemo(() => kpis(currentUser, userType), [currentUser, userType]);
	const KPIdates = useMemo(() => {
		const timeAdjustedStartDate = startDate
			.clone()
			.add(startDate.utcOffset(), 'minutes')
			.startOf('day');
		const timeAdjustedEndDate = endDate.clone().add(endDate.utcOffset(), 'minutes').endOf('day');

		return kpiDates(currentUser, userType, timeAdjustedStartDate, timeAdjustedEndDate);
	}, [currentUser, userType, startDate, endDate]);

	const cleanedFilters = useMemo(() => {
		const filterValue = nullSafeGetOrElse('filterValue', view, {});
		return cleanFilters(filterValue);
	}, [nullSafeGet('filterValue', view)]);

	return (
		<div
			style={{
				marginTop: '1em',
				width: '100%',
				position: 'relative',
			}}
		>
			<ADHeader
				addWidget={showAddWidgetModal}
				editDashboard={showEditModal}
				name={nullSafeGet('nameGiven', currentUser)}
			/>

			<Row style={{ display: 'flex', justifyContent: 'start' }}>
				<span
					className="truncate"
					style={{
						color: 'rgba(0, 0, 0, 0.85)',
						letterSpacing: '0.5px',
						fontSize: '17px',
						fontWeight: 400,
						marginLeft: 16,
					}}
				>
					Latest
				</span>
			</Row>
			<Row key={3} style={{ margin: '0.5em -8px' }} gutter={16}>
				{getObjectValues(KPIs).map((kpi, idx) => (
					<DashboardKPICard kpi={kpi} startDate={startDate} endDate={endDate} />
				))}
			</Row>
			<Row style={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
				<DashboardDateRangePicker
					value={[startDate, endDate]}
					onChange={(d) => {
						if (d[0] && d[1]) {
							setStartDate(d[0]);
							setEndDate(d[1]);
						}
					}}
					disabledDate={(current) => current && current > moment()}
				/>
				<ViewSelection selectedView={view} setSelectedView={setView} />
			</Row>
			{view ? (
				<Row style={{ marginTop: 8, marginBottom: 8, marginLeft: 12 }}>
					<AttentionDashboardFilterConfig view={view} setView={setView} />
				</Row>
			) : null}
			<Row
				key={3}
				style={{ marginTop: 4, marginBottom: '0.5em', marginLeft: '-8px', marginRight: '-8px' }}
				gutter={16}
			>
				{getObjectValues(KPIdates).map((kpi, idx) => (
					<DashboardKPICard
						kpi={kpi}
						startDate={startDate}
						endDate={endDate}
						showComparison={true}
						filters={cleanedFilters}
					/>
				))}
			</Row>
			{dashboardViewsLoading && !nullSafeGet('savedFilterViews.length', attentionDashboard) ? (
				<OverlayContainer>
					<Spin />
				</OverlayContainer>
			) : (
				<div>
					<div>
						{attentionDashboard && attentionDashboard.savedFilterViews
							? attentionDashboard.savedFilterViews.map((savedView) => (
									<div className="rowSpacing" key={savedView.id}>
										<Card bodyStyle={{ padding: 0 }}>
											<EachWidget id={savedView.id} savedView={savedView} />
										</Card>
									</div>
							  ))
							: null}
					</div>
					{addWidgetModalVisible && (
						<ADAddWidgetPopup
							filterIds={attentionDashboard.savedFilterViewIds}
							roleType={ROLE_TYPES.BUYER}
							onCancel={hideAddWidgetModal}
						/>
					)}
					{editWidgetModalVisible && (
						<AttentionDashboardEditPopup
							savedFilterViews={attentionDashboard.savedFilterViews}
							onCancel={hideEditModal}
							updateView={updateSavedFilterView}
							refreshDashboard={getAttentionDashboard}
							saveDashboard={onSaveDashboard}
						/>
					)}
				</div>
			)}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	attentionDashboard: state.attention_dashboards.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getAttentionDashboard: () => getAttentionDashboard(dispatch, ownProps.userType),
	saveAttentionDashboard: (entity) => updateAttentionDashboard(dispatch, ownProps.userType, entity),
	updateSavedFilterView: (entity) =>
		dispatch(savedFilterViewsRestCrudThunksForBuyer.update(entity)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(AttentionDashboardIndexPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
